import { Label, assets } from '@round/whitelabel-ui';
import { Input } from '@round/whitelabel-ui';
import styles from '../SongFormAudios.module.css';
import { Button } from '@round/whitelabel-ui';
import TiktokAudiosTable, { TiktokAudiosTableRow } from './Table/TiktokAudiosTable';
import { useMemo, useState } from 'react';
import { showNotification } from 'helpers';
import { SongFormValues } from '../../SongForm';
import { useFormikContext } from 'formik';
import { useTiktokAudios as usePlatformTiktokAudios } from '@round/platforms/tiktok';
import { useTiktokAudios as useCreatorbaseTiktokAudios } from '@round/creatorbase-helpers/api-hooks';
import { useFindOrCreateTiktokAudio } from 'Modules/Plans/hooks/queries/tiktokAudio/useFindOrCreateTiktokAudio';
import uniq from 'lodash/uniq';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { tiktokQueries } from '@round/api';

const { Plus: PlusIcon } = assets;

type Props = {
    staticTiktokAudios?: number[];
};

const SongFormTiktokAudios = ({ staticTiktokAudios }: Props) => {
    const { values, setFieldValue } = useFormikContext<SongFormValues>();

    const ids = uniq([...(staticTiktokAudios ?? []), ...values.tiktok_audios]);
    const {
        data: platformData,
        isLoading,
        isError,
    } = usePlatformTiktokAudios(
        { id: ids.join(','), page_size: ids.length },
        { enabled: ids.length > 0, placeholderData: keepPreviousData }
    );
    const { data: creatorbaseData } = useCreatorbaseTiktokAudios(
        { id: ids.join(','), page_size: ids.length },
        { enabled: ids.length > 0, placeholderData: keepPreviousData }
    );

    const audios = useMemo(() => {
        const platformResults = platformData?.results ?? [];
        if (!creatorbaseData?.results.length) {
            return platformResults;
        }
        const creatorbaseMap = new Map(creatorbaseData.results.map((a) => [a.id, a]));
        return platformResults.map((audio) => {
            const cb = creatorbaseMap.get(audio.id);
            return cb ? { ...audio, video_count: cb.video_count } : audio;
        });
    }, [platformData, creatorbaseData]);

    const { findOrCreate, isLoading: isAddAudioLoading } = useFindOrCreateTiktokAudio();
    const [tiktokAudioUrl, setTiktokAudioUrl] = useState('');
    const queryClient = useQueryClient();

    const handleAddAudio = async () => {
        if (!tiktokAudioUrl) {
            return;
        }

        try {
            const audio = await findOrCreate(tiktokAudioUrl);

            if ([...values.tiktok_audios, ...(staticTiktokAudios || [])].includes(audio.id)) {
                showNotification('Audio already added', 'error');
                return;
            }

            // Seed the next list-query key with the new audio so the row renders
            // immediately instead of waiting for the keepPreviousData refetch.
            const newIds = uniq([...(staticTiktokAudios ?? []), audio.id, ...values.tiktok_audios]);
            const seededResults = [audio, ...(platformData?.results ?? [])];
            queryClient.setQueryData(
                tiktokQueries.audio.keys.list({ id: newIds.join(','), page_size: newIds.length }),
                { count: seededResults.length, next: null, previous: null, results: seededResults }
            );

            setTiktokAudioUrl('');
            setFieldValue('tiktok_audios', [audio.id, ...values.tiktok_audios]);
            showNotification('Audio added successfully', 'info');
        } catch (e) {
            const message = e instanceof Error ? e.message : `Couldn't add audio`;
            showNotification(message, 'error');
        }
    };

    const handleRemoveAudio = (audioId: number) => {
        setFieldValue(
            'tiktok_audios',
            values.tiktok_audios.filter((id) => id !== audioId)
        );
    };

    const rows = (() => {
        let rows: TiktokAudiosTableRow[] = [];

        //if the song is changed to unreleased, we will hide the static audios
        if (values.status === 'released') {
            rows =
                staticTiktokAudios?.reduce<TiktokAudiosTableRow[]>((acc, id) => {
                    const audio = audios?.find((audio) => audio.id === id);
                    return audio ? acc.concat({ ...audio, isStatic: true }) : acc;
                }, []) ?? [];
        }

        return rows.concat(
            values.tiktok_audios
                .filter((id) => !staticTiktokAudios?.includes(id))
                .reduce<TiktokAudiosTableRow[]>((acc, id) => {
                    const audio = audios?.find((audio) => audio.id === id);
                    return audio ? acc.concat({ ...audio, isStatic: false }) : acc;
                }, []) ?? []
        );
    })();

    return (
        <div className={styles.platformAudiosContainer}>
            <div className={styles.field}>
                <Label>TikTok Audio</Label>
                <div className={styles.audioInputRow}>
                    <Input
                        placeholder="Enter TikTok audio URL"
                        className={styles.audioInput}
                        value={tiktokAudioUrl}
                        onChange={(e) => setTiktokAudioUrl(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                void handleAddAudio();
                            }
                        }}
                        disabled={isError}
                    />
                    <Button
                        appearance="primary"
                        onClick={handleAddAudio}
                        disabled={isAddAudioLoading || isLoading || isError}
                        isLoading={isAddAudioLoading}
                        type="button"
                    >
                        <PlusIcon className={styles.plusIcon} />
                    </Button>
                </div>
            </div>

            <TiktokAudiosTable data={rows} isLoading={isLoading} onRemoveAudio={handleRemoveAudio} hasError={isError} />
        </div>
    );
};

export default SongFormTiktokAudios;
